// 168B §1c — ONE CELL FOR "WHAT THIS CLIENT'S STANDING LOOKS LIKE".
//
// 141 §2.3 put the RULE in one place (`clientMatterStanding`, clientMatters.mjs):
// a client's standing in a matter is their own link status where they have been
// given one, else the matter's own status. `ClientCasesCard` then drew it, and
// Laurent RULED on 2026-08-27 exactly how — a prospect MATTER first, then a
// status the client OWNS in the link vocabulary's colours, then a status
// INHERITED from the matter in the absence pair, muted, because the two
// vocabularies overlap in wording (a case whose status is `pending` displays
// "Suspended", which is also a link status) and the muting is what tells a
// reader whose standing they are looking at.
//
// Ruling 1 of 168B puts the same value on the Clients LIST — the client's most
// recent standing across their matters. Brief 168B §1c: "REUSE that cell, do
// not draw a third." So the three arms move here, unchanged, and both surfaces
// mount them. A copy of them in `ecfg.tsx` is exactly the defect 115 closed for
// the Dashboard's rows, 117 §2a for the subtask pill and 141 §2.2 for this very
// pill's colours: two renderers for one thing, and every later fix landing in
// one of them.
//
// EXTRACT, DON'T IMPROVE (rule 3). The elements below are
// `ClientCasesCard.tsx`'s, prop for prop — including the `null` fourth arm and
// the `fontFamily: "inherit"` that makes StatusPill match `.status`'s metric.
// The ternary chain is written as early returns only because
// `sonarjs/no-nested-conditional` is a warning and this file must carry NONE:
// `scripts/lint/eslint-ratchet.js` allows a file OUTSIDE its baseline no
// warnings at all. Same reason the label arrives as a resolved STRING rather
// than as the `(code) => string` its callers hold — a named parameter inside a
// function TYPE is what the base `no-unused-vars` rule cannot see through, and
// there is no ignore pattern configured (measured, `eslint.config.mjs`).
import React from "react";
import StatusPill from "../components/StatusPill";
import { linkStatusCfg } from "./linkStatus";
import type { TFn } from "./linkStatus";
import { FS } from "./tokens";

/** `clientMatterStanding`'s answer, as much of it as the drawing needs. The
 *  module that computes it is JavaScript, so the shape is named here rather
 *  than imported. `fromLink` is the whole reason it is a record and not a
 *  string: it says WHICH of the two vocabularies answered, and the two wear
 *  different pills. */
export interface ClientStanding {
  status: string | null;
  date: Date | null;
  fromLink: boolean;
}

/** WHAT THE CLIENTS LIST'S CLIENT-STATUS COLUMN SHOWS FOR ONE ROW — the rule,
 *  once, so the cell and the FILTER cannot disagree about it (168B GLANCE
 *  ruling 2, measured by Laurent on staging: filtering on Prospect returned
 *  nobody). `code` is what the column shows expressed as a value the filter can
 *  compare; `prospect` is whether the Prospect pill is the thing drawn. */
export interface ClientStatusShown {
  prospect: boolean;
  code: string | null;
}

export function clientStatusShown(
  client: { isProspect?: boolean | null } | null | undefined,
  sum:
    | {
        mostRecentStatus?: string | null;
        mostRecentMatter?: { isProspect?: boolean | null } | null;
      }
    | null
    | undefined,
): ClientStatusShown {
  /* THE PERSON'S TAG WINS OVER ANY STANDING [RULED Laurent 2026-09-05, at the
     168B glance, overturning §1c's "two marks, two places"]. "Prospect" on a
     person says they are not a client of the firm AT ALL, which is a stronger
     statement than how one of their matters stands — so it is the whole answer
     for that row, and it is why the tag left the Name cell. The matter's own
     tag (140 §1.2) keeps its arm underneath, as §1c built it.
     "Prospect" is the link vocabulary's own value (`KNOWN_STATUSES[0]`), not a
     label: it is compared, never shown. */
  /* `||`, NOT `??`, and the difference is load-bearing: `Client.isProspect` is
     a Boolean column, so the API serves `false` — and `false ?? x` is `false`,
     which would make the matter arm dead code. `Boolean(...)` on each side is
     what keeps the linter quiet without changing that. */
  const prospect =
    Boolean(client?.isProspect) || Boolean(sum?.mostRecentMatter?.isProspect);
  if (prospect) return { prospect, code: "Prospect" };
  return { prospect, code: sum?.mostRecentStatus ?? null };
}

/* 206C §4.1 — THE CLIENT RECORD'S OWN « ARCHIVED » CHIP, MOVED HERE from
   `ecfg.tsx`'s client `detailBadge` (Phase 36 (D)), so the provisioning list
   can wear the same chip rather than a copy of it. EXTRACT, DON'T IMPROVE
   (rule 3): the element is `detailBadge`'s, prop for prop — its grey ground
   included, a literal that predates §4.9 and moved untouched — and the
   client's detail pane renders it byte for byte as before. It sits beside
   the Prospect pill for the reason both are here: they are the two marks a
   client record can carry. */
export function clientArchivedChip(t: TFn): React.ReactElement {
  return (
    <span
      className="btn"
      style={{
        padding: "2px 7px",
        fontSize: FS.s10,
        background: "rgba(120,120,120,.15)",
        color: "var(--tm)",
        cursor: "default",
      }}
      title={t("client.archived_hint", {
        fallback: "Archived — hidden from the default list, still searchable",
      })}
    >
      {t("client.archived", { fallback: "Archived" })}
    </span>
  );
}

export function clientStandingPill({
  standing,
  isProspect,
  t,
  inheritedLabel,
}: {
  standing: ClientStanding | null | undefined;
  /** draw the Prospect pill outright. WHICH tag that stands for is the
   *  caller's: the client record's matter card passes the MATTER's (140 §1.2),
   *  one row per matter; the Clients list passes `clientStatusShown`'s answer,
   *  which is the PERSON's tag first and the winning matter's after it. */
  isProspect: boolean;
  t: TFn;
  /** the matter status already resolved to its taxonomy label, for the third
   *  arm. Only that arm reads it. */
  inheritedLabel: string;
}): React.ReactNode {
  if (isProspect)
    return (
      <span className="status status-prospect">
        {t("matters.prospect.badge", { fallback: "Prospect" })}
      </span>
    );
  const st = standing ? standing.status : null;
  if (standing && standing.fromLink && st)
    return (
      <StatusPill
        cfg={linkStatusCfg(t, st)}
        style={{
          border: "none",
          fontSize: FS.s10,
          fontWeight: 500,
          fontFamily: "inherit",
        }}
      />
    );
  if (st)
    return (
      <span
        className="status"
        style={{ color: "var(--tm)", background: "var(--bi)" }}
      >
        {inheritedLabel}
      </span>
    );
  return null;
}
